import { useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import useAuthStore from '../store/authStore'
import { authService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import AdminPasswordRequirements from '../components/AdminPasswordRequirements'
import {
  getAdminPasswordPolicyError,
  isAdminPasswordValid,
} from '../utils/adminPasswordPolicy'

const inviteMessages = {
  expired:
    'This invitation has expired. Please ask an administrator to send you a new invitation.',
  used: 'This invitation has already been used. Please log in.',
  superseded: 'This invitation is no longer valid. A newer invitation has been issued.',
  deactivated: 'This link is invalid or no longer available.',
  invalid: 'This link is invalid or no longer available.',
}

const AcceptInvitePage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setToken = useAuthStore((s) => s.setToken)
  const [token, setOpaqueToken] = useState(null)
  const [status, setStatus] = useState('loading')
  const [message, setMessage] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const t = searchParams.get('token')
    if (t) {
      setOpaqueToken(t)
      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      window.history.replaceState({}, '', url.pathname + url.search)
    } else {
      setOpaqueToken('')
    }
  }, [searchParams])

  useEffect(() => {
    if (token === null) return
    if (!token) {
      setStatus('invalid')
      setMessage(inviteMessages.invalid)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await authService.validateInvite(token)
        if (cancelled) return
        if (res.status === 'valid') setStatus('valid')
        else {
          setStatus(res.status || 'invalid')
          setMessage(inviteMessages[res.status] || res.message || inviteMessages.invalid)
        }
      } catch {
        if (!cancelled) {
          setStatus('invalid')
          setMessage(inviteMessages.invalid)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  useEffect(() => {
    if (status !== 'loading' && status !== 'valid') {
      const t = setTimeout(() => navigate('/login', { replace: true }), 4000)
      return () => clearTimeout(t)
    }
  }, [status, navigate])

  const canSubmit =
    isAdminPasswordValid(password) && password === confirm && !submitting

  const onSubmit = async (e) => {
    e.preventDefault()
    setError('')
    const policyError = getAdminPasswordPolicyError(password)
    if (policyError) {
      setError(policyError)
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      const data = await authService.acceptInvite(token, password)
      setToken(data.access_token)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not accept invitation.'))
      setSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ bgcolor: 'rgba(15,18,28,0.85)', color: '#fff' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Accept invitation
            </Typography>
            {status === 'loading' || token === null ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress sx={{ color: '#fff' }} />
              </Box>
            ) : status !== 'valid' ? (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {message}
                </Alert>
                <Link component={RouterLink} to="/login" sx={{ color: '#90caf9' }}>
                  Return to sign in
                </Link>
              </>
            ) : (
              <Box component="form" onSubmit={onSubmit}>
                {error ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                ) : null}
                <AdminPasswordRequirements password={password} dark />
                <TextField
                  fullWidth
                  required
                  type={showPassword ? 'text' : 'password'}
                  label="Create password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  sx={{
                    mb: 2,
                    input: { color: '#fff' },
                    label: { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(0,0,0,0.25)',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                      '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((v) => !v)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  required
                  type={showConfirm ? 'text' : 'password'}
                  label="Confirm password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  sx={{
                    mb: 2.5,
                    input: { color: '#fff' },
                    label: { color: 'rgba(255,255,255,0.7)' },
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(0,0,0,0.25)',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                      '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showConfirm ? 'Hide confirm password' : 'Show confirm password'
                          }
                          onClick={() => setShowConfirm((v) => !v)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={!canSubmit}
                  sx={{
                    py: 1.25,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: canSubmit ? '#7c4dff' : 'rgba(124, 77, 255, 0.35)',
                    color: canSubmit ? '#fff' : 'rgba(255,255,255,0.55)',
                    '&:hover': {
                      bgcolor: canSubmit ? '#651fff' : 'rgba(124, 77, 255, 0.35)',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(124, 77, 255, 0.35)',
                      color: 'rgba(255,255,255,0.55)',
                    },
                  }}
                >
                  Set password and continue
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}

export default AcceptInvitePage
